import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { EXAM_BOOKINGS_WRITE_ROLES, hasRole } from '@/auth/roles';
import { getExamBookings } from '@/api/examBookings.api';
import { getExamPricing } from '@/api/finance.api';
import { ApiError } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import { ExamSectionPanel } from '@/pages/exam-bookings/ExamSectionPanel';
import { formatDateTime } from '@/lib/format';
import { EXAM_SECTIONS, FINANCE_READ_ROLES } from '@/types/shared';
import type {
  ExamBookingResponse,
  ExamPriceRow,
  ExamSection,
  ExamSectionRow,
} from '@/types/shared';
import './ExamBookingsDetailPage.css';

export function ExamBookingsDetailPage() {
  const { t, i18n } = useTranslation();
  const { studentCode } = useParams<{ studentCode: string }>();
  const { user } = useAuth();
  const canEdit = hasRole(user?.role, EXAM_BOOKINGS_WRITE_ROLES);

  const [booking, setBooking] = useState<ExamBookingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // T126 (17-finance-ux.md §6.3/§6.6) — the section's CURRENT native cost/price, fetched ONCE for
  // the whole page (all 4 sections in one call) and handed down to each panel purely to compute
  // the client-side live-EGP-preview while entering a FRESH reservation. Gated to roles that can
  // actually reach `GET /finance/exam-pricing` (Admin + Accounts Officer) — Exam Coordinator
  // cannot read pricing config at all (§A §7), so this fetch is skipped for them entirely rather
  // than firing a doomed 403; the panel simply omits the preview in that case (non-blocking,
  // §6.6/§10.8 — the persisted, authoritative snapshot is unaffected either way).
  const [examPricing, setExamPricing] = useState<Partial<Record<ExamSection, ExamPriceRow>> | null>(
    null,
  );

  useEffect(() => {
    if (!studentCode) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getExamBookings(studentCode)
      .then((data) => {
        if (cancelled) return;
        setBooking(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const apiError = ApiError.fromUnknown(err);
        setLoadError(
          apiError.code === 'NOT_FOUND'
            ? t('examBookings.detail.notFound')
            : t('examBookings.detail.loadError'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentCode, t]);

  useEffect(() => {
    if (!canEdit || !hasRole(user?.role, FINANCE_READ_ROLES)) return;
    let cancelled = false;
    getExamPricing()
      .then((res) => {
        if (cancelled) return;
        const bySection: Partial<Record<ExamSection, ExamPriceRow>> = {};
        for (const row of res.sections) bySection[row.examSection] = row;
        setExamPricing(bySection);
      })
      .catch(() => {
        // Best-effort only — the live preview is a non-authoritative convenience; a failed/absent
        // fetch just means no preview renders, never a page-level error (§6.3).
      });
    return () => {
      cancelled = true;
    };
  }, [canEdit, user?.role]);

  const orderedSections = useMemo<ExamSectionRow[]>(() => {
    if (!booking) return [];
    const bySection = new Map(booking.sections.map((s) => [s.examSection, s]));
    return EXAM_SECTIONS.map((section) => bySection.get(section)).filter((s): s is ExamSectionRow =>
      Boolean(s),
    );
  }, [booking]);

  const remaining = booking ? booking.sections.length - booking.bookedCount : 0;

  function handleSectionSaved(updated: ExamSectionRow) {
    setBooking((prev) => {
      if (!prev) return prev;
      const sections = prev.sections.map((s) =>
        s.examSection === updated.examSection ? updated : s,
      );
      const bookedCount = sections.filter(
        (s) => s.bookingStatus === 'Booked' || s.bookingStatus === 'Completed',
      ).length;
      return { ...prev, sections, bookedCount };
    });
  }

  if (loading) {
    return <p className="fa-form-loading">{t('common.loading')}</p>;
  }

  if (loadError || !booking) {
    return (
      <div>
        <Banner tone="danger" assertive>
          {loadError ?? t('examBookings.detail.notFound')}
        </Banner>
        <Link to="/exam-bookings" className="fa-back-link">
          <span className="fa-back-link__arrow" aria-hidden="true" />
          {t('examBookings.detail.backToList')}
        </Link>
      </div>
    );
  }

  const { student } = booking;

  return (
    <section className="fa-exam-bookings-detail">
      <Link to="/exam-bookings" className="fa-back-link">
        <span className="fa-back-link__arrow" aria-hidden="true" />
        {t('examBookings.detail.backToList')}
      </Link>

      <header className="fa-exam-bookings-detail__header">
        <div className="fa-exam-bookings-detail__heading">
          <div>
            <h1 className="fa-exam-bookings-detail__title">
              <span className="fa-ltr-isolate">{student.studentCode}</span>
              {' — '}
              {student.nameAr}
            </h1>
            <p className="fa-exam-bookings-detail__subtitle">{student.nameEn}</p>
            <div className="fa-exam-bookings-detail__badges">
              {!canEdit && (
                <span className="fa-chip fa-chip--neutral">
                  {t('examBookings.detail.readOnlyBadge')}
                </span>
              )}
            </div>
          </div>
        </div>
        <Link to={`/students/${student.studentCode}`} className="fa-btn fa-btn--secondary">
          {t('examBookings.detail.viewStudent')}
        </Link>
      </header>

      <section className="fa-form-section">
        <h2 className="fa-form-section__title">{t('examBookings.detail.summaryTitle')}</h2>
        <p className="fa-exam-bookings-detail__summary">
          <bdi>
            {t('examBookings.detail.summaryText', {
              booked: booking.bookedCount,
              total: booking.sections.length,
              remaining,
            })}
          </bdi>
        </p>
      </section>

      <section className="fa-form-section fa-exam-bookings-detail__sections">
        <h2 className="fa-form-section__title">{t('examBookings.detail.sectionsTitle')}</h2>
        <div className="fa-exam-bookings-detail__sections-list">
          {orderedSections.map((section) => (
            <ExamSectionPanel
              key={section.examSection}
              studentCode={student.studentCode}
              row={section}
              canEdit={canEdit}
              onSaved={handleSectionSaved}
              pricing={examPricing?.[section.examSection] ?? null}
            />
          ))}
        </div>
      </section>

      <section className="fa-form-section fa-exam-bookings-detail__audit">
        <h2 className="fa-form-section__title">{t('examBookings.detail.auditTitle')}</h2>
        <div className="fa-exam-bookings-detail__audit-grid">
          <p>
            <span className="fa-exam-bookings-detail__audit-label">
              {t('examBookings.detail.addedBy')}
            </span>
            <span>
              {booking.createdBy?.fullName ?? t('examBookings.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(booking.createdAt, i18n.language)}</bdi>
            </span>
          </p>
          <p>
            <span className="fa-exam-bookings-detail__audit-label">
              {t('examBookings.detail.updatedBy')}
            </span>
            <span>
              {booking.updatedBy?.fullName ?? t('examBookings.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(booking.updatedAt, i18n.language)}</bdi>
            </span>
          </p>
        </div>
      </section>
    </section>
  );
}
